import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router';
import { useApi } from '../../hooks/useApi';
import { hentSoknadsStatuser } from '../../utils/api';
import { HotjarMaskert } from '../../components/HotjarMaskert';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { GodkjennTegnkombinasjonSteg } from './GodkjennTegnkombinasjonSteg';
import { BetalRettighetSteg } from './BetalRettighetSteg';
import { BestillSkiltSteg } from './BestillSkiltSteg';
import { HentSkiltSteg } from './HentSkiltSteg';
import { KnyttSkiltSteg } from './KnyttSkiltSteg';

import './StatusSoknad.scss';
import { useApptekster } from '../../contexts/ApptekstContext';

type TegnkombinasjonParams = {
    id: string;
};

export const StatusSoknad: React.FC = () => {
    const t = useApptekster();
    let { id } = useParams<TegnkombinasjonParams>();

    const { hent, laster, data } = useApi(hentSoknadsStatuser, {
        caching: false,
    });

    useEffect(() => {
        hent();
    }, [hent]);

    const soknad = data
        ?.filter(
            (soknad) =>
                `${soknad.rettighetId}` === id || `${soknad.soknadId}` === id,
        )
        .shift();

    if (data?.length && !soknad) {
        return <Navigate to={'/statusoversikt'} />;
    }

    if (laster) {
        return (
            <div aria-live="polite">
                <SVVLayoutBanner>
                    <SVVSpinner size="large">
                        {t('personlig-kjennemerke.delt.laster')}
                    </SVVSpinner>
                </SVVLayoutBanner>
            </div>
        );
    }

    function LenkeTilbakeKnapp() {
        if (!data || data.length === 1) {
            return (
                <SVVBreadcrumbLink
                    href="#/"
                    ariaLabel={t('personlig-kjennemerke.snarvei.meny')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.meny')}
                </SVVBreadcrumbLink>
            );
        }
        return (
            <SVVBreadcrumbLink
                href="#/statusoversikt"
                ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                className="intern-link"
            >
                {t('personlig-kjennemerke.snarvei.status')}
            </SVVBreadcrumbLink>
        );
    }

    return (
        <>
            <div className="statusoversikt">
                <SVVLayoutBanner>
                    <LenkeTilbakeKnapp />
                    <h1 className="spacing-bottom--small">
                        {t('personlig-kjennemerke.delt.personlig-skilt')}:{' '}
                        <HotjarMaskert>{soknad?.tegnkombinasjon}</HotjarMaskert>
                    </h1>
                    <h2 className="svv-h3 bottom-spacing-medium">
                        {t('personlig-kjennemerke.status.underoverskrift')}
                    </h2>
                </SVVLayoutBanner>
            </div>

            {soknad && (
                <SVVLayoutBanner secondary>
                    <div
                        key={soknad.soknadId}
                        className="status-soknad container"
                    >
                        <ol className="statusliste">
                            <GodkjennTegnkombinasjonSteg
                                sakStatus={soknad.sakStatus}
                                soknadId={soknad.soknadId}
                                tegnkombinasjon={soknad.tegnkombinasjon}
                                datoStatusEndret={soknad.datoStatusEndret}
                            />

                            <BetalRettighetSteg
                                sakStatus={soknad.sakStatus}
                                soknadId={soknad.soknadId}
                            />

                            <BestillSkiltSteg
                                rettighetId={soknad.rettighetId}
                                sakStatus={soknad.sakStatus}
                                tegnkombinasjon={soknad.tegnkombinasjon}
                            />

                            <HentSkiltSteg sakStatus={soknad.sakStatus} />

                            <KnyttSkiltSteg
                                sakStatus={soknad.sakStatus}
                                tegnkombinasjon={soknad.tegnkombinasjon}
                                rettighetId={soknad.rettighetId}
                                kjennemerke={soknad.aliasFor}
                            />
                        </ol>
                    </div>
                </SVVLayoutBanner>
            )}
        </>
    );
};
